import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import {
  primaryGroups,
  moreGroups,
  groupPath,
  servicePath,
} from "../data/services";
import { legal } from "../data/legal";

function DesktopDropdown({ group }) {
  return (
    <div className="group relative">
      <button className="flex items-center gap-1 py-2 text-sm font-semibold text-acblue/90 transition hover:text-acblue">
        {group.title}
        <ChevronDown className="h-4 w-4" />
      </button>
      <div className="invisible absolute left-0 top-full z-30 w-72 translate-y-1 rounded-2xl bg-white p-2 opacity-0 shadow-xl ring-1 ring-acblue/10 transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
        <Link
          to={groupPath(group)}
          className="block rounded-xl px-3 py-2 text-xs font-semibold uppercase tracking-wide text-acAccent hover:bg-acBg"
        >
          All {group.title}
        </Link>
        {group.services.map((s) => (
          <Link
            key={s.slug}
            to={servicePath(group, s)}
            className="block rounded-xl px-3 py-2 text-sm text-acBlack/80 hover:bg-acBg hover:text-acblue"
          >
            {s.title}
          </Link>
        ))}
      </div>
    </div>
  );
}

function MoreDropdown() {
  return (
    <div className="group relative">
      <button className="flex items-center gap-1 py-2 text-sm font-semibold text-acblue/90 transition hover:text-acblue">
        More
        <ChevronDown className="h-4 w-4" />
      </button>
      <div className="invisible absolute left-0 top-full z-30 w-72 translate-y-1 rounded-2xl bg-white p-2 opacity-0 shadow-xl ring-1 ring-acblue/10 transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
        {moreGroups.map((group) => (
          <Link
            key={group.slug}
            to={groupPath(group)}
            className="block rounded-xl px-3 py-2 text-sm text-acBlack/80 hover:bg-acBg hover:text-acblue"
          >
            {group.title}
          </Link>
        ))}
        <Link
          to="/about/"
          className="block rounded-xl px-3 py-2 text-sm text-acBlack/80 hover:bg-acBg hover:text-acblue"
        >
          About
        </Link>
        <Link
          to="/how-it-works/"
          className="block rounded-xl px-3 py-2 text-sm text-acBlack/80 hover:bg-acBg hover:text-acblue"
        >
          How it works
        </Link>
      </div>
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-acblue/10 bg-white/95 backdrop-blur">
      <div className="container-page flex items-center justify-between py-4">
        <Link to="/" className="flex items-center lg:mr-8">
          <img
            src={`${process.env.PUBLIC_URL}/mm_logo.png`}
            alt={legal.brandName}
            className="h-9 w-auto"
          />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          {primaryGroups.map((g) => (
            <DesktopDropdown key={g.slug} group={g} />
          ))}
          <MoreDropdown />
          <NavLink
            to="/contact/"
            className="py-2 text-sm font-semibold text-acblue/90 hover:text-acblue"
          >
            Contact
          </NavLink>
        </nav>

        <div className="hidden items-center gap-3 lg:ml-4 lg:flex lg:border-l lg:border-acblue/10 lg:pl-6">
          {/* Existing clients only. Separate app on its own host, so a plain
              anchor, not a Router Link. */}
          <a href={legal.portalUrl} className="btn-primary">
            Login
          </a>
          <Link to="/eligibility-quiz/" className="btn-ghost">
            Check eligibility
          </Link>
          <Link to="/book-consultation/" className="btn-ghost">
            Book a consultation
          </Link>
        </div>

        <button
          className="lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? (
            <X className="h-6 w-6 text-acblue" />
          ) : (
            <Menu className="h-6 w-6 text-acblue" />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-acblue/10 bg-white lg:hidden">
          <div className="container-page space-y-4 py-4">
            {[...primaryGroups, ...moreGroups].map((g) => (
              <div key={g.slug}>
                <Link
                  to={groupPath(g)}
                  onClick={() => setOpen(false)}
                  className="block py-1 text-sm font-semibold text-acblue"
                >
                  {g.title}
                </Link>
              </div>
            ))}
            <Link
              to="/contact/"
              onClick={() => setOpen(false)}
              className="block py-1 text-sm font-semibold text-acblue"
            >
              Contact
            </Link>
            <div className="flex flex-col gap-3 pt-2">
              <a
                href={legal.portalUrl}
                onClick={() => setOpen(false)}
                className="btn-primary"
              >
                Login
              </a>
              <Link
                to="/eligibility-quiz/"
                onClick={() => setOpen(false)}
                className="btn-ghost"
              >
                Check eligibility
              </Link>
              <Link
                to="/book-consultation/"
                onClick={() => setOpen(false)}
                className="btn-ghost"
              >
                Book a consultation
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
